// components/PostCreator/PostCreatorModalContainer.tsx
import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { usePostModals } from "./hooks/usePostModals";
import { PostCreatorModal } from "./PostCreatorModal";
import { ReelCreatorModal } from "./ReelCreatorModal";
import { ContentTypeSelector } from "./ContentTypeSelector";
import { PostItem } from "@/core/Post/PostItem.model";
import { postCacheUtils } from "./hooks/usePostsQuery";
import { mediaCacheUtils } from "@/core/MediaLibrary/hooks/useMediaQuery";
import postsService from "./services/postsService";
import { ideaKeys } from "@/domains/Ideas/types";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";

export const PostCreatorModalContainer: React.FC = () => {
  const queryClient = useQueryClient();
  const ui = useAppStore((state) => state.ui);
  const selectedClient = useAppStore((state) => state.selectedClient);
  const { closeModal } = usePostModals();
  const [selectedContentType, setSelectedContentType] = useState<
    "post" | "reel" | null
  >(null);

  const isOpen = ui.activeModal?.name === "post-creator";
  const modalProps = ui.activeModal?.props || {};
  const {
    mode = "create",
    editingPost = null,
    contentType = null,
    preSelectedMediaItems = undefined,
    linkToIdeaId = null,
    linkToIdeaClientId = null,
  } = modalProps;

  // Invalidate media cache when modal opens to ensure fresh data
  useEffect(() => {
    if (isOpen && selectedClient?.id) {
      mediaCacheUtils.invalidateAllMedia(selectedClient.id);
    }
  }, [isOpen, selectedClient?.id]);

  // Guard: Modal requires a selected client
  if (isOpen && !selectedClient) {
    return (
      <Modal
        isOpen={true}
        onClose={closeModal}
        title="No Client Selected"
        size="md"
        footer={<Button onClick={closeModal}>Close</Button>}
      >
        <div className="p-6">
          <p className="text-gray-600">Please select a client before creating posts.</p>
        </div>
      </Modal>
    );
  }

  // If editing existing post, skip content type selection
  const isEditMode = mode === "edit" && editingPost;
  const isEditingReel =
    isEditMode && (editingPost.postType === "reel" || contentType === "reel");
  const showContentTypeSelector =
    isOpen && !isEditMode && !selectedContentType && !contentType;
  const showPostCreator =
    isOpen &&
    (selectedContentType === "post" ||
      contentType === "post" ||
      (isEditMode && !isEditingReel));
  const showReelCreator =
    isOpen &&
    (selectedContentType === "reel" || contentType === "reel" || isEditingReel);

  const handleSuccess = async (postId: string, response: any) => {
    console.log(
      `${selectedContentType === "reel" ? "Reel" : "Post"} ${mode === "edit" ? "updated" : "created"} successfully:`,
      {
        postId,
        response,
      },
    );

    // Update React Query cache with the new/updated post
    if (response?.post && selectedClient) {
      const postItem = PostItem.fromBackend(response.post, 'admin', selectedClient.id);
      postCacheUtils.upsertPost(selectedClient.id, postItem);
    } else {
      console.error('Post created but response.post is missing:', response);
    }

    // Link post to idea if creating from an idea modal
    if (linkToIdeaId && linkToIdeaClientId && mode !== "edit") {
      try {
        await postsService.linkToIdea(linkToIdeaClientId, postId, linkToIdeaId);
        queryClient.invalidateQueries({ queryKey: ideaKeys.detail(linkToIdeaId) });
        queryClient.invalidateQueries({ queryKey: ideaKeys.list(linkToIdeaClientId) });
      } catch (err) {
        console.error("Failed to link post to idea:", err);
      }
    }

    closeModal();
    setSelectedContentType(null); // Reset for next time
  };

  const handleError = (error: string) => {
    console.error(
      `Failed to ${mode === "edit" ? "update" : "create"} ${selectedContentType === "reel" ? "reel" : "post"}:`,
      error,
    );
  };

  const handleClose = () => {
    closeModal();
    setSelectedContentType(null); // Reset for next time
  };

  const handleSelectPost = () => {
    setSelectedContentType("post");
  };

  const handleSelectReel = () => {
    setSelectedContentType("reel");
  };

  if (!isOpen) return null;

  // Show content type selector first (unless editing or content type is specified)
  if (showContentTypeSelector) {
    return (
      <ContentTypeSelector
        isOpen={true}
        onClose={handleClose}
        onSelectPost={handleSelectPost}
        onSelectReel={handleSelectReel}
      />
    );
  }

  // Show post creator
  if (showPostCreator) {
    return (
      <PostCreatorModal
        isOpen={true}
        onClose={handleClose}
        clientId={selectedClient?.id || null}
        editingPost={editingPost}
        preSelectedMediaItems={preSelectedMediaItems}
        linkToIdeaId={linkToIdeaId}
        onSuccess={handleSuccess}
        onError={handleError}
      />
    );
  }

  // Show reel creator
  if (showReelCreator) {
    return (
      <ReelCreatorModal
        isOpen={true}
        onClose={handleClose}
        clientId={selectedClient?.id || null}
        editingPost={editingPost}
        onSuccess={handleSuccess}
        onError={handleError}
      />
    );
  }

  return null;
};
